import { useGetAllMajorNames, useGetUniversitiesLight, useGetUniversityResults } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Select from '@components/V4/Select/Select';
import { useDebounce } from '@hooks';
import { GOAL_STATUS_OPTIONS } from '@shared/constants';
import { GoalStatusTypes } from '@shared/enums';
import { Spin } from 'antd';
import { stringify } from 'csv-stringify/sync';
import moment from 'moment';
import { useState } from 'react';

const currentYear = moment().year();
const YEAR_OPTIONS = [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7].map((offset) => {
  const year = (currentYear + offset).toString();
  return { value: year, label: year };
});

const UniversityResultsTab = () => {
  const [applicationStages, setApplicationStages] = useState<GoalStatusTypes[]>([]);
  const [majors, setMajors] = useState<string[]>([]);
  const [universities, setUniversities] = useState<string[]>([]);
  const [graduationYears, setGraduationYears] = useState<string[]>([]);
  const [universitySearch, setUniversitySearch] = useState('');

  const debouncedUniversitySearch = useDebounce(universitySearch, 500);

  const { data: allMajors } = useGetAllMajorNames();
  const { data: allUniversities, isLoading: isUniversitiesLoading } = useGetUniversitiesLight({
    search: debouncedUniversitySearch,
    limit: 20,
  });

  const { data: results, isLoading } = useGetUniversityResults({
    applicationStages,
    majors,
    universities,
    graduationYears,
  });

  const totalStudents = (results ?? []).reduce((sum, result) => sum + result.count, 0);

  const handleExportCSV = () => {
    if (!results?.length) return;
    const rows: string[][] = [
      ['University', 'Students'],
      ...results.map((result) => [result.universityName, String(result.count)]),
    ];
    const csvContent = stringify(rows, { header: false });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `university-results-${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-6 font-raleway text-white">
      <div className="flex flex-wrap gap-3">
        <Select
          className="max-w-64"
          variant="secondary"
          label="Application Stage"
          mode="multiple"
          value={applicationStages.length ? applicationStages : undefined}
          options={GOAL_STATUS_OPTIONS}
          onSelect={(value) =>
            setApplicationStages((prev) => Array.from(new Set([...prev, value as GoalStatusTypes])))
          }
          onDeselect={(value) =>
            setApplicationStages((prev) => prev.filter((stage) => stage !== value))
          }
          onClear={() => setApplicationStages([])}
          placeholder="Any stage"
        />
        <Select
          className="max-w-64"
          variant="secondary"
          label="Major"
          mode="multiple"
          value={majors.length ? majors : undefined}
          options={(allMajors ?? [])
            .filter((major) => !!major._id)
            .map((major) => ({ value: major._id as string, label: major.name }))}
          onSelect={(value) => setMajors((prev) => Array.from(new Set([...prev, value as string])))}
          onDeselect={(value) => setMajors((prev) => prev.filter((id) => id !== value))}
          onClear={() => setMajors([])}
          placeholder="Any major"
        />
        <Select
          className="max-w-64"
          variant="secondary"
          label="University"
          mode="multiple"
          value={universities.length ? universities : undefined}
          filterOption={() => true}
          searchValue={universitySearch}
          onSearch={setUniversitySearch}
          onBlur={() => setUniversitySearch('')}
          onSelect={(value) =>
            setUniversities((prev) => Array.from(new Set([...prev, value as string])))
          }
          onDeselect={(value) => setUniversities((prev) => prev.filter((id) => id !== value))}
          onClear={() => setUniversities([])}
          options={allUniversities?.map((university) => ({
            value: university._id,
            label: university.name,
          }))}
          notFoundContent={isUniversitiesLoading ? <LoadingSpinner className="mx-auto" /> : null}
          placeholder="Search universities"
        />
        <Select
          className="max-w-64"
          variant="secondary"
          label="Graduation Year"
          mode="multiple"
          value={graduationYears.length ? graduationYears : undefined}
          options={YEAR_OPTIONS}
          onSelect={(value) =>
            typeof value === 'string' &&
            setGraduationYears((prev) => Array.from(new Set([...prev, value])))
          }
          onDeselect={(value) =>
            setGraduationYears((prev) => prev.filter((year) => year !== value))
          }
          onClear={() => setGraduationYears([])}
          placeholder="Any year"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spin size="large" />
        </div>
      ) : results && results.length ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-customGrey">
              {results.length} {results.length === 1 ? 'university' : 'universities'} ·{' '}
              {totalStudents} {totalStudents === 1 ? 'student' : 'students'}
            </p>
            <Button variant="secondary" buttonText="Export to CSV" onClick={handleExportCSV} />
          </div>
          <ul className="flex flex-col gap-3">
            {results.map((result) => (
              <li
                key={result.universityId}
                className="flex items-center justify-between gap-3 rounded-xl bg-surfaceObject p-4"
              >
                <span className="font-semibold text-white">{result.universityName}</span>
                <span className="shrink-0 rounded-full bg-customPrimary/20 px-2.5 py-0.5 text-xs font-semibold text-customPrimary">
                  {result.count} {result.count === 1 ? 'student' : 'students'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="rounded-2xl bg-surfaceObject p-8 text-center text-sm text-customGrey">
          No university applications match these filters.
        </div>
      )}
    </div>
  );
};

export default UniversityResultsTab;
